/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import "./Collection.css";

export default function Collection() {
  const images = Array.from({ length: 30 }, (_, i) => require(`../images/${String(i + 1).padStart(2, "0")}.png`));

  const artist = {
    name: "Sailing Ships",
    username: "@By Jubi",
    profileImg: require("../images/Avatar.png"),
  };

  // Holds the index of the enlarged image, or null
  const [enlargedIndex, setEnlargedIndex] = useState(null);
  // Controls avatar enlargement
  const [isAvatarEnlarged, setIsAvatarEnlarged] = useState(false);
  // Controls button visibility on hover/click
  const [showButtons, setShowButtons] = useState(false);
  const buttonAreaRef = useRef(null);
  const hideButtonsTimeout = useRef();

  // Escape closes modal, Left/Right arrow keys navigate
  const handleKeyDown = useCallback(
    (e) => {
      if (enlargedIndex !== null) {
        if (e.key === "Escape") {
          setEnlargedIndex(null);
        } else if (e.key === "ArrowRight") {
          setEnlargedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
          setShowButtons(true);
        } else if (e.key === "ArrowLeft") {
          setEnlargedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
          setShowButtons(true);
        }
      } else if (isAvatarEnlarged && e.key === "Escape") {
        setIsAvatarEnlarged(false);
      }
    },
    [enlargedIndex, images.length, isAvatarEnlarged]
  ); // Avatar enlargement handlers
  const handleAvatarClick = () => {
    setIsAvatarEnlarged(true);
  };

  const handleCloseAvatarEnlarged = () => {
    setIsAvatarEnlarged(false);
  };

  // Unified scroll lock for both image and avatar modals
  useEffect(() => {
    if (enlargedIndex !== null || isAvatarEnlarged) {
      // Store original scroll position
      const scrollY = window.scrollY;

      // Add class to body to control other elements' z-index
      document.body.classList.add("modal-open");
      if (isAvatarEnlarged) {
        document.body.classList.add("avatar-modal-open");
      }

      // Disable scrolling on both html and body
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      // Remove class from body
      document.body.classList.remove("modal-open");
      document.body.classList.remove("avatar-modal-open");

      // Restore scrolling and position
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      // Scroll to collection section when exiting enlarge mode
      const collectionSection = document.querySelector(".collection");
      if (collectionSection) {
        collectionSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.classList.remove("modal-open");
      document.body.classList.remove("avatar-modal-open");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [enlargedIndex, isAvatarEnlarged]);

  // Unified keyboard events for both modals
  useEffect(() => {
    if (enlargedIndex !== null || isAvatarEnlarged) {
      window.addEventListener("keydown", handleKeyDown);
      if (enlargedIndex !== null) {
        setShowButtons(false); // Reset visibility whenever modal opens
      }
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        clearTimeout(hideButtonsTimeout.current);
      };
    }
  }, [enlargedIndex, isAvatarEnlarged, handleKeyDown]);

  // Close modal on any click
  const handleOverlayClick = () => setEnlargedIndex(null);

  // Close modal handler for X button
  const handleCloseModal = (e) => {
    e.stopPropagation();
    setEnlargedIndex(null);
  };

  // Show on hover/click, hide after mouse leaves the button area (including button click)
  const handleButtonAreaMouseEnter = () => {
    clearTimeout(hideButtonsTimeout.current);
    setShowButtons(true);
  };

  const handleButtonAreaMouseLeave = () => {
    hideButtonsTimeout.current = setTimeout(() => setShowButtons(false), 100);
  };
  // The pill button click handler
  const handleButtonClick = (e, direction) => {
    e.stopPropagation();
    if (direction === "prev") {
      setEnlargedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    } else if (direction === "next") {
      setEnlargedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
    setShowButtons(false); // Hide after click
  }; // Twitter share handler
  const handleTwitterShare = (e, imageIndex) => {
    e.stopPropagation();
    const imageNumber = imageIndex + 1;
    const tweetText = `🚢 Check out this amazing NFT from the Sailing Ships collection! Ship #${imageNumber} ⛵ 

View the full collection at: ${window.location.origin}

#NFT #SailingShips #DigitalArt #NFTCollection #CryptoArt #XRPL`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, "_blank", "width=550,height=420");
  };

  return (
    <section className="collection" id="collection">
      <div className="container-fluid">
        <div className="row">
          <h1>
            <span>GENESIS COLLECTION</span>
          </h1>
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="collection-card">
              <div className="collection-card-header">
                <button
                  onClick={handleAvatarClick}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <img src={artist.profileImg} alt="Profile" />
                </button>
                <div className="collection-card-header-info">
                  <h3
                    style={{
                      background: "linear-gradient(to right, #DAA520, #FFD700, #FFA500, #FF8C00, #B8860B)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {artist.name}
                  </h3>
                  <p
                    style={{
                      background: "linear-gradient(to right, #DAA520, #FFD700, #FFA500, #FF8C00, #B8860B)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {artist.username}
                  </p>
                </div>
              </div>
              <div className="collection-card-images-grid">
                {images.map((img, i) => (
                  <div className="collection-card-image" key={`collection-img-${i}-${img.slice(-10)}`}>
                    <button
                      onClick={() => setEnlargedIndex(i)}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: "zoom-in",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      <img
                        src={img}
                        alt={`Collection ${i + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>{" "}
      </div>{" "}
      {/* Render modal using Portal to ensure it appears above all other content */}
      {enlargedIndex !== null &&
        createPortal(
          <div
            className="enlarge-modal-overlay"
            onClick={handleOverlayClick}
            onWheel={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEnlargedIndex(null);
              }
            }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ position: "fixed", inset: 0, zIndex: 9999 }}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div
              className="enlarge-modal-content-hoverframe"
              ref={buttonAreaRef}
              onMouseEnter={handleButtonAreaMouseEnter}
              onMouseLeave={handleButtonAreaMouseLeave}
              onClick={handleOverlayClick}
              role="button"
              tabIndex="0"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleOverlayClick();
                }
              }}
            >
              <img
                src={images[enlargedIndex]}
                alt={`Enlarged ${enlargedIndex + 1}`}
                style={{ pointerEvents: "none" }}
              />
              <button
                className={`nav-pill-btn nav-pill-prev${showButtons ? " show" : ""}`}
                onClick={(e) => handleButtonClick(e, "prev")}
                aria-label="Previous image"
                tabIndex={0}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <polygon points="13,5 5,10 13,15" fill="#000" />
                </svg>
              </button>
              <button
                className={`nav-pill-btn nav-pill-next${showButtons ? " show" : ""}`}
                onClick={(e) => handleButtonClick(e, "next")}
                aria-label="Next image"
                tabIndex={0}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <polygon points="7,5 15,10 7,15" fill="#000" />
                </svg>
              </button>
              <button
                className="twitter-share-modal-btn show"
                onClick={(e) => handleTwitterShare(e, enlargedIndex)}
                aria-label="Share on Twitter"
                title="Share this NFT on Twitter"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
                <span>Share</span>
              </button>
              <button
                className="modal-close-btn"
                onClick={handleCloseModal}
                aria-label="Close modal"
                title="Close"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="#000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>,
          document.body
        )}
      {/* Avatar Enlarged Modal */}
      {isAvatarEnlarged && (
        <div className="avatar-modal">
          <button
            className="modal-backdrop-button"
            onClick={handleCloseAvatarEnlarged}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                handleCloseAvatarEnlarged();
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "transparent",
              border: "none",
              cursor: "default",
              zIndex: -1,
            }}
            aria-label="Close avatar modal"
            tabIndex={-1}
          />
          <div className="avatar-modal-content">
            <div className="avatar-container">
              <img src={artist.profileImg} alt="Enlarged Profile" />
              <button
                className="close-modal-btn"
                onClick={handleCloseAvatarEnlarged}
                title="Close"
                aria-label="Close avatar modal"
              >
                ✕
              </button>
            </div>
            <div className="avatar-enlarged-details">
              <h3
                style={{
                  background: "linear-gradient(to right, #DAA520, #FFD700, #FFA500, #FF8C00, #B8860B)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontFamily: '"Lora", serif',
                }}
              >
                {artist.name}
              </h3>
              <p
                style={{
                  background: "linear-gradient(to right, #DAA520, #FFD700, #FFA500, #FF8C00, #B8860B)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontFamily: '"Lora", serif',
                }}
              >
                {artist.username}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
